import type { ApiClient } from "./client";
import type { Account } from "../types/api";
import type {
  AccountInvite,
  AgentProfile,
  Channel,
  ChannelMessage,
  ChannelTask,
  ChannelVisibility,
  Computer,
  ComputerType,
  PairingCode,
  Workspace,
  WorkspaceMember,
  WorkspaceEvent,
  TaskOwnerType,
  TaskStatus,
} from "../types/collab";

const ACCESS_KEY = "cc_access_token";

/** The current access token the AuthProvider keeps in localStorage. */
function currentAccessToken(): string {
  try {
    return localStorage.getItem(ACCESS_KEY) ?? "";
  } catch {
    return "";
  }
}

function authed<T>(client: ApiClient, path: string, init?: RequestInit): Promise<T> {
  return client.requestJson<T>(path, init);
}

export function listComputers(
  client: ApiClient,
  accountId: string
): Promise<{ computers: Computer[]; latestVersion?: string }> {
  return authed(client, `/v1/accounts/${accountId}/computers`);
}

export function createPairingCode(client: ApiClient, accountId: string): Promise<PairingCode> {
  return authed(client, `/v1/accounts/${accountId}/pairing-codes`, { method: "POST" });
}

// --- computer actions (local computers) ------------------------------------

export function restartComputer(client: ApiClient, accountId: string, computerId: string): Promise<{ ok: boolean }> {
  return authed(client, `/v1/accounts/${accountId}/computers/${computerId}/restart`, { method: "POST" });
}

export function upgradeComputer(client: ApiClient, accountId: string, computerId: string): Promise<{ ok: boolean }> {
  return authed(client, `/v1/accounts/${accountId}/computers/${computerId}/upgrade`, { method: "POST" });
}

export function setComputerBrowser(
  client: ApiClient,
  accountId: string,
  computerId: string,
  enabled: boolean
): Promise<{ ok: boolean; browser: NonNullable<Computer["browser"]> }> {
  return authed(client, `/v1/accounts/${accountId}/computers/${computerId}/browser`, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

export function deleteComputer(client: ApiClient, accountId: string, computerId: string): Promise<{ ok: boolean }> {
  return authed(client, `/v1/accounts/${accountId}/computers/${computerId}`, { method: "DELETE" });
}

// --- members + invites -----------------------------------------------------

export function listMembers(client: ApiClient, accountId: string): Promise<{ members: WorkspaceMember[] }> {
  return authed(client, `/v1/accounts/${accountId}/members`);
}

export function removeMember(client: ApiClient, accountId: string, userId: string): Promise<{ ok: boolean }> {
  return authed(client, `/v1/accounts/${accountId}/members/${userId}`, { method: "DELETE" });
}

export function createInvite(client: ApiClient, accountId: string): Promise<{ invite: AccountInvite; token: string }> {
  return authed(client, `/v1/accounts/${accountId}/invites`, { method: "POST" });
}

export function acceptInvite(client: ApiClient, token: string): Promise<{ accountId: string }> {
  return authed(client, `/v1/invites/accept`, { method: "POST", body: JSON.stringify({ token }) });
}

export function listWorkspaces(client: ApiClient): Promise<{ workspaces: Workspace[] }> {
  return authed(client, `/v1/workspaces`);
}

export function createWorkspace(client: ApiClient, name: string, slug?: string): Promise<{ account: Account; slug: string }> {
  return authed(client, `/v1/workspaces`, { method: "POST", body: JSON.stringify({ name, slug }) });
}

// --- @username -------------------------------------------------------------

export function getMyUsername(client: ApiClient): Promise<{ username: string | null }> {
  return authed(client, `/v1/username`);
}

export function setMyUsername(client: ApiClient, username: string): Promise<{ username: string }> {
  return authed(client, `/v1/username`, { method: "PUT", body: JSON.stringify({ username }) });
}

/** Registration stores fresh tokens before AuthProvider can expose its client. */
export async function setMyUsernameAfterRegistration(baseUrl: string, username: string): Promise<void> {
  const response = await fetch(`${baseUrl}/v1/username`, {
    method: "PUT",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${currentAccessToken()}`,
    },
    body: JSON.stringify({ username }),
  });
  if (!response.ok) throw new Error(`request failed (${response.status})`);
}

/** Public availability check (no auth) — used during registration. */
export async function checkUsername(baseUrl: string, username: string): Promise<{ valid: boolean; available: boolean }> {
  const res = await fetch(`${baseUrl}/v1/usernames/available?u=${encodeURIComponent(username)}`);
  if (!res.ok) return { valid: false, available: false };
  return (await res.json()) as { valid: boolean; available: boolean };
}

// --- agent profiles (Phase 3) ----------------------------------------------

export function listAgents(client: ApiClient, accountId: string): Promise<{ agents: AgentProfile[] }> {
  return authed(client, `/v1/accounts/${accountId}/agent-profiles`);
}

export function createAgent(
  client: ApiClient,
  accountId: string,
  input: {
    handle: string;
    displayName: string;
    lane?: string;
    persona?: string;
    runtime?: string;
    workspace?: string;
    computerType?: ComputerType;
    computerId?: string;
  }
): Promise<{ agent: AgentProfile }> {
  return authed(client, `/v1/accounts/${accountId}/agent-profiles`, { method: "POST", body: JSON.stringify(input) });
}

export function deleteAgent(client: ApiClient, accountId: string, id: string): Promise<{ ok: boolean }> {
  return authed(client, `/v1/accounts/${accountId}/agent-profiles/${id}`, { method: "DELETE" });
}

// --- channels + messages (Phase 2) -----------------------------------------

export function listChannels(client: ApiClient, accountId: string): Promise<{ channels: Channel[] }> {
  return authed(client, `/v1/accounts/${accountId}/channels`);
}

export function createChannel(
  client: ApiClient,
  accountId: string,
  input: { name: string; topic?: string; visibility?: ChannelVisibility }
): Promise<{ channel: Channel }> {
  return authed(client, `/v1/accounts/${accountId}/channels`, { method: "POST", body: JSON.stringify(input) });
}

export function updateChannel(
  client: ApiClient,
  accountId: string,
  channelId: string,
  patch: { name?: string; topic?: string; archived?: boolean }
): Promise<{ channel: Channel }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function listMessages(
  client: ApiClient,
  accountId: string,
  channelId: string,
  opts: { before?: string; limit?: number } = {}
): Promise<{ messages: ChannelMessage[] }> {
  const q = new URLSearchParams();
  if (opts.before) q.set("before", opts.before);
  if (opts.limit) q.set("limit", String(opts.limit));
  const suffix = q.toString() ? `?${q}` : "";
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/messages${suffix}`);
}

/** A message's thread replies, oldest→newest. */
export function listThread(
  client: ApiClient,
  accountId: string,
  channelId: string,
  messageId: string
): Promise<{ messages: ChannelMessage[] }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/messages/${messageId}/thread`);
}

export function postMessage(
  client: ApiClient,
  accountId: string,
  channelId: string,
  body: string,
  threadId?: string
): Promise<{ message: ChannelMessage; task?: ChannelTask }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/messages`, {
    method: "POST",
    body: JSON.stringify({ body, threadId }),
  });
}

// --- channel tasks (Phase 4) -----------------------------------------------

export interface TaskPatch {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  ownerType?: TaskOwnerType | null;
  ownerId?: string | null;
}

export function listTasks(
  client: ApiClient,
  accountId: string,
  channelId: string
): Promise<{ tasks: ChannelTask[] }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/tasks`);
}

export function createTask(
  client: ApiClient,
  accountId: string,
  channelId: string,
  input: { title: string; description?: string; ownerType?: TaskOwnerType | null; ownerId?: string | null }
): Promise<{ task: ChannelTask }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/tasks`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function convertMessageToTask(
  client: ApiClient,
  accountId: string,
  channelId: string,
  messageId: string
): Promise<{ task: ChannelTask; created: boolean }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/messages/${messageId}/task`, {
    method: "POST",
  });
}

export function updateTask(
  client: ApiClient,
  accountId: string,
  channelId: string,
  taskId: string,
  patch: TaskPatch
): Promise<{ task: ChannelTask }> {
  return authed(client, `/v1/accounts/${accountId}/channels/${channelId}/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export interface WorkspaceStream {
  close(): void;
}

export interface WorkspaceStreamOptions {
  reconnectDelay?: (attempt: number) => number;
  onReconnect?: () => void;
}

export function workspaceReconnectDelay(attempt: number): number {
  return Math.min(1_000 * 2 ** attempt, 15_000);
}

/** Keep the workspace stream alive across sleep, network changes, and token expiry. */
export function openWorkspaceStream(
  client: ApiClient,
  accountId: string,
  onEvent: (event: WorkspaceEvent) => void,
  options: WorkspaceStreamOptions = {}
): WorkspaceStream {
  let stopped = false;
  let attempt = 0;
  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const scheduleReconnect = () => {
    if (stopped || timer) return;
    timer = setTimeout(() => {
      timer = null;
      connect();
    }, (options.reconnectDelay ?? workspaceReconnectDelay)(attempt++));
  };

  const connect = () => {
    if (stopped) return;
    const ws = new WebSocket(client.workspaceStreamUrl(accountId));
    socket = ws;
    ws.onopen = () => {
      if (attempt > 0) options.onReconnect?.();
      attempt = 0;
    };
    ws.onmessage = (event) => {
      try {
        onEvent(JSON.parse(event.data) as WorkspaceEvent);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = (event) => {
      if (socket === ws) socket = null;
      if (stopped) return;
      if (event.code === 4401) {
        // An authenticated REST probe refreshes an expired access token before
        // the next socket URL is created. Invalid refresh tokens sign the user out.
        void client.me().catch(() => undefined).finally(scheduleReconnect);
        return;
      }
      scheduleReconnect();
    };
  };

  connect();
  return {
    close() {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
      socket?.close();
      socket = null;
    },
  };
}
